// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Download, FileText, Users, Loader2, Calendar, Trophy, Upload } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { generateTeamQRCode } from '@/utils/qrGenerator';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface Template {
  id: string;
  name: string;
  description: string;
  layout: 'one-page-per-team' | 'condensed' | 'detailed';
  selectedFields: string[];
  headerText: string;
  footerText: string;
  logoUrl?: string;
}

interface EventPacketManagerProps {
  eventId: string;
  eventName: string;
}

export const EventPacketManager: React.FC<EventPacketManagerProps> = ({ eventId, eventName }) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<string>('all');
  const [ageGroups, setAgeGroups] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSettingPurchasable, setIsSettingPurchasable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [teamsSummary, setTeamsSummary] = useState({ totalTeams: 0, totalPlayers: 0 });
  const [teams, setTeams] = useState<any[]>([]);
  const [currentPacketUrl, setCurrentPacketUrl] = useState<string | null>(null);

  useEffect(() => {
    fetchTemplates();
    fetchEventTeams();
    fetchCurrentPacketUrl();
  }, [eventId]);

  const fetchCurrentPacketUrl = async () => {
    const { data } = await supabase
      .from('events')
      .select('purchasable_packet_url')
      .eq('id', eventId)
      .single();
    
    setCurrentPacketUrl(data?.purchasable_packet_url || null);
  };

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('packet_templates')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;

      const mapped: Template[] = (data || []).map(t => ({
        id: t.id,
        name: t.name,
        description: t.description || '',
        layout: t.layout || 'one-page-per-team',
        selectedFields: t.columns || [],
        headerText: t.header_text || '',
        footerText: t.footer_text || '',
        logoUrl: t.logo_url || undefined,
      }));

      setTemplates(mapped);
      if (mapped.length > 0) {
        setSelectedTemplate(mapped[0].id);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchEventTeams = async () => {
    setLoading(true);
    try {
      // Get teams from event_team_rosters
      const { data: eventRosters, error } = await supabase
        .from('event_team_rosters')
        .select('team_id')
        .eq('event_id', eventId);

      if (error) throw error;

      const uniqueTeamIds = [...new Set((eventRosters || []).map(r => r.team_id))];

      if (uniqueTeamIds.length === 0) {
        setTeams([]);
        setAgeGroups([]);
        setTeamsSummary({ totalTeams: 0, totalPlayers: 0 });
        setLoading(false);
        return;
      }

      // Fetch team details
      const { data: teamsData } = await supabase
        .from('coach_teams')
        .select('id, name, head_coach_name, head_coach_email, head_coach_cell, age_group')
        .in('id', uniqueTeamIds);

      // Count players per team
      const teamCounts: Record<string, number> = {};
      for (const teamId of uniqueTeamIds) {
        const { count } = await supabase
          .from('event_team_rosters')
          .select('id', { count: 'exact', head: true })
          .eq('event_id', eventId)
          .eq('team_id', teamId);
        teamCounts[teamId] = count || 0;
      }

      const teamsWithCounts = (teamsData || []).map(team => ({
        ...team,
        roster_count: teamCounts[team.id] || 0,
      }));

      setTeams(teamsWithCounts);

      // Extract unique age groups
      const groups = [...new Set(teamsWithCounts.map(t => t.age_group).filter(Boolean))] as string[];
      setAgeGroups(groups);

      // Calculate summary
      const filteredTeams = selectedAgeGroup === 'all' 
        ? teamsWithCounts 
        : teamsWithCounts.filter(t => t.age_group === selectedAgeGroup);
      
      setTeamsSummary({
        totalTeams: filteredTeams.length,
        totalPlayers: filteredTeams.reduce((sum, t) => sum + (t.roster_count || 0), 0),
      });
    } catch (error) {
      console.error('Error fetching event teams:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const filteredTeams = selectedAgeGroup === 'all' 
      ? teams 
      : teams.filter(t => t.age_group === selectedAgeGroup);
    
    setTeamsSummary({
      totalTeams: filteredTeams.length,
      totalPlayers: filteredTeams.reduce((sum, t) => sum + (t.roster_count || 0), 0),
    });
  }, [selectedAgeGroup, teams]);

  const getFilteredTeams = () => {
    if (selectedAgeGroup === 'all') return teams;
    return teams.filter(t => t.age_group === selectedAgeGroup);
  };

  const generateAndDownloadPackets = async (setPurchasable = false) => {
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }

    const filteredTeams = getFilteredTeams();
    if (filteredTeams.length === 0) {
      toast.error('No teams to generate packets for');
      return;
    }

    if (setPurchasable) {
      setIsSettingPurchasable(true);
    } else {
      setIsGenerating(true);
    }

    try {
      const template = templates.find(t => t.id === selectedTemplate);
      if (!template) throw new Error('Template not found');

      const teamIds = filteredTeams.map(t => t.id);

      // Fetch roster data with player details
      const { data: rosterData, error: rosterError } = await supabase
        .from('event_team_rosters')
        .select(`
          roster_member_id,
          team_id,
          coach_team_rosters!inner (
            id,
            first_name,
            last_name,
            email,
            cell_phone,
            jersey_number,
            position,
            grad_year,
            profile_id
          )
        `)
        .eq('event_id', eventId)
        .in('team_id', teamIds);

      if (rosterError) throw rosterError;

      // Group by team
      const teamGroups: Record<string, any[]> = {};
      for (const entry of rosterData || []) {
        if (!teamGroups[entry.team_id]) {
          teamGroups[entry.team_id] = [];
        }
        teamGroups[entry.team_id].push(entry.coach_team_rosters);
      }

      // Generate QR codes for players with profiles
      for (const teamId of Object.keys(teamGroups)) {
        for (const player of teamGroups[teamId]) {
          if (player.profile_id) {
            try {
              player.qrCode = await generateTeamQRCode(player.profile_id);
            } catch (e) {
              player.qrCode = null;
            }
          }
        }
      }

      // Helper functions
      const getFieldDisplayName = (fieldName: string): string => {
        return fieldName
          .replace(/_/g, ' ')
          .replace(/([A-Z])/g, ' $1')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')
          .trim();
      };

      const getDynamicColumns = () => {
        const templateFields = template.selectedFields || [];
        const headerFields = ['head_coach_name', 'head_coach_email', 'head_coach_cell', 'name', 'age_group'];
        const filteredFields = templateFields.filter(field => !headerFields.includes(field));
        return [...filteredFields, 'profile'];
      };

      const getFieldValue = (player: any, fieldName: string): string => {
        if (fieldName === 'profile') return 'profile';
        if (player[fieldName] !== undefined) return player[fieldName] || 'N/A';
        return 'N/A';
      };

      // Generate HTML
      let allTeamsHTML = '';
      const teamsArray = filteredTeams.map(team => ({
        team,
        players: teamGroups[team.id] || [],
      }));

      for (let i = 0; i < teamsArray.length; i++) {
        const { team, players } = teamsArray[i];
        const pageBreak = i > 0 ? 'style="page-break-before: always;"' : '';

        allTeamsHTML += `
          <div class="team-page" ${pageBreak}>
            <div class="header">
              ${template.logoUrl ? `<img src="${template.logoUrl}" alt="Logo" class="template-logo" />` : ''}
              <div class="header-title">${template.headerText || template.name}</div>
              <div class="team-info">${team.name}${team.age_group ? ` - ${team.age_group}` : ''}</div>
              ${team.head_coach_name ? `
              <div class="coach-info">
                <strong>Coach:</strong> ${team.head_coach_name}
                ${team.head_coach_email ? ` | <strong>Email:</strong> ${team.head_coach_email}` : ''}
                ${team.head_coach_cell ? ` | <strong>Phone:</strong> ${team.head_coach_cell}` : ''}
              </div>
              ` : ''}
            </div>
            
            <table class="roster-table">
              <thead>
                <tr>
                  ${getDynamicColumns().map(col => 
                    col === 'profile' ? '<th>Profile</th>' : `<th>${getFieldDisplayName(col)}</th>`
                  ).join('')}
                </tr>
              </thead>
              <tbody>
                ${players.map(player => `
                  <tr>
                    ${getDynamicColumns().map(col => {
                      if (col === 'profile') {
                        return `<td style="text-align: center;">
                          ${player.qrCode 
                            ? `<img src="${player.qrCode}" alt="QR" style="width: 50px; height: 50px;" />`
                            : 'No Profile'
                          }
                        </td>`;
                      }
                      return `<td>${getFieldValue(player, col)}</td>`;
                    }).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            <div class="footer">
              <p>${template.footerText || 'RecruitLook Team Roster'}</p>
              <p>Page ${i + 1} of ${teamsArray.length}</p>
            </div>
          </div>
        `;
      }

      const fullHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Helvetica Neue', Arial, sans-serif; background: #f5f5f5; }
            .team-page { background: white; padding: 40px; margin: 20px auto; max-width: 900px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #002E62; padding-bottom: 20px; }
            .template-logo { max-height: 60px; margin-bottom: 10px; }
            .header-title { font-size: 28px; font-weight: bold; color: #002E62; margin-bottom: 8px; }
            .team-info { font-size: 20px; color: #BC8D09; font-weight: 600; }
            .coach-info { color: #444; font-size: 14px; margin-top: 8px; }
            .roster-table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            .roster-table th { background: #002E62; color: white; padding: 12px; text-align: left; font-size: 13px; }
            .roster-table td { padding: 10px 12px; border-bottom: 1px solid #e0e0e0; font-size: 13px; }
            .roster-table tr:nth-child(even) { background: #f9f9f9; }
            .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px; }
            @media print { .team-page { margin: 0; box-shadow: none; } }
          </style>
        </head>
        <body>${allTeamsHTML}</body>
        </html>
      `;

      // Create hidden container for rendering
      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.left = '-9999px';
      container.style.width = '900px';
      container.innerHTML = fullHTML;
      document.body.appendChild(container);

      // Generate PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pages = container.querySelectorAll('.team-page');

      for (let i = 0; i < pages.length; i++) {
        const canvas = await html2canvas(pages[i] as HTMLElement, { scale: 2, useCORS: true });
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const imgWidth = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
      }

      document.body.removeChild(container);

      const filename = `${eventName.replace(/[^a-z0-9]/gi, '-')}-roster-packet.pdf`;
      const pdfBlob = pdf.output('blob');

      if (setPurchasable) {
        // Upload to storage and set as purchasable
        const { error: uploadError } = await supabase.storage
          .from('purchasable-packets')
          .upload(`events/${eventId}/${filename}`, pdfBlob, {
            contentType: 'application/pdf',
            upsert: true,
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('purchasable-packets')
          .getPublicUrl(`events/${eventId}/${filename}`);

        await supabase
          .from('events')
          .update({ purchasable_packet_url: publicUrl })
          .eq('id', eventId);

        setCurrentPacketUrl(publicUrl);
        toast.success('Packet set as purchasable for this event');
      } else {
        // Download locally
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
        toast.success(`Generated packet with ${teamsArray.length} teams`);
      }
    } catch (error) {
      console.error('Error generating packets:', error);
      toast.error('Failed to generate packets');
    } finally {
      setIsGenerating(false);
      setIsSettingPurchasable(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Packet Status */}
      {currentPacketUrl && (
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="font-medium text-emerald-900">Purchasable packet is set</p>
                  <p className="text-sm text-emerald-700">Users can purchase this packet</p>
                </div>
              </div>
              <Button variant="outline" size="sm" asChild>
                <a href={currentPacketUrl} target="_blank" rel="noopener noreferrer">
                  <Download className="h-4 w-4 mr-2" />
                  View Current
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Configuration */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Trophy className="h-5 w-5" />
              Template
            </CardTitle>
            <CardDescription>Select packet template</CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
              <SelectTrigger>
                <SelectValue placeholder="Select template" />
              </SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5" />
              Age Group Filter
            </CardTitle>
            <CardDescription>Filter teams by age group</CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={selectedAgeGroup} onValueChange={setSelectedAgeGroup}>
              <SelectTrigger>
                <SelectValue placeholder="All age groups" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Age Groups</SelectItem>
                {ageGroups.map(ag => (
                  <SelectItem key={ag} value={ag}>{ag}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      {/* Summary & Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Generate Packets</CardTitle>
          <CardDescription>Create roster packets for selected teams</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-muted/50 rounded-lg text-center">
              <div className="text-3xl font-bold text-primary">{teamsSummary.totalTeams}</div>
              <div className="text-sm text-muted-foreground">Teams</div>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg text-center">
              <div className="text-3xl font-bold text-primary">{teamsSummary.totalPlayers}</div>
              <div className="text-sm text-muted-foreground">Players</div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => generateAndDownloadPackets(false)}
              disabled={isGenerating || isSettingPurchasable || teamsSummary.totalTeams === 0}
              className="flex-1"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF ({teamsSummary.totalTeams} teams)
                </>
              )}
            </Button>
            <Button
              variant="secondary"
              onClick={() => generateAndDownloadPackets(true)}
              disabled={isGenerating || isSettingPurchasable || teamsSummary.totalTeams === 0}
              className="flex-1"
            >
              {isSettingPurchasable ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Set as Purchasable Packet
                </>
              )}
            </Button>
          </div>

          {teamsSummary.totalTeams === 0 && (
            <p className="text-sm text-muted-foreground text-center">
              No teams with rosters found for this event
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
